import React from "react";
import { Container } from "../../../../components";
import { Button, Icon, Tooltip } from "@telia/teddy";
import classnames from "classnames";
import { useSelector } from "react-redux";
import PAGES from "../../../../../../constants/pages";
import { RootState } from "../../../../../../models";
import BackLink from "../../../../checkout/components/atoms/BackLink/BackLink";
import { hasHandsetLease } from "../../../../checkout/state/selectors/subscriptionsSelectors";
import {
    isCustomerStepComplete as isCustomerStepCompleteSelector,
    getIsPrepaid,
} from "../../../state/selectors/customerSelectors";
import { getIsD2DOrTMDealer } from "../../../state/selectors/selectors";
import "./ContinueSection.less";

interface ContinueButtonProps {
    path: string;
}

export const NextButton: React.FC<{
    disabled?: boolean;
    id?: string;
    loading?: boolean;
    onClick?: () => Promise<void> | void;
    title?: string;
    tooltip?: string;
}> = ({ title = "Neste", disabled, id, loading, tooltip, onClick }) => {
    const button = (
        <Button id={id} variant="primary" onClick={onClick} disabled={disabled} loading={loading}>
            {title}
            <Icon name="arrow-right" aria-hidden="true" focusable="false" />
        </Button>
    );

    return disabled && tooltip ? (
        <Tooltip>
            <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
            <Tooltip.Content>{tooltip}</Tooltip.Content>
        </Tooltip>
    ) : (
        button
    );
};

const ContinueButton: React.FC<ContinueButtonProps> = ({ path }) => {
    const isCustomerStepComplete = useSelector((state: RootState) => isCustomerStepCompleteSelector(state));
    const isPrepaid = useSelector((state: RootState) => getIsPrepaid(state));
    const isD2DOrTM = useSelector((state: RootState) => getIsD2DOrTMDealer(state));
    const hasHandsetLeaseInCart = useSelector((state: RootState) => hasHandsetLease(state));
    const shouldGoToEquipment = !isPrepaid && !isD2DOrTM && hasHandsetLeaseInCart;

    switch (path) {
        case PAGES.RETAILER.ORDER.CUSTOMER.URL:
            return (
                <NextButton
                    id="neste-abonnement-btn"
                    title="Neste: abonnement"
                    onClick={() => window.location.assign(PAGES.RETAILER.ORDER.SUBSCRIPTION.URL)}
                    disabled={!isCustomerStepComplete}
                    tooltip="Dette steget må fullføres før du kan gå videre med abonnement og utstyr."
                />
            );
        case PAGES.RETAILER.ORDER.SUBSCRIPTION.URL:
            return (
                <NextButton
                    disabled={!isCustomerStepComplete}
                    title={shouldGoToEquipment ? "Neste: Utstyr" : "Neste: Tilleggstjenester"}
                    onClick={() =>
                        window.location.assign(
                            shouldGoToEquipment
                                ? PAGES.RETAILER.ORDER.EQUIPMENT.URL
                                : PAGES.RETAILER.ORDER.ADDITIONAL_SERVICES.URL,
                        )
                    }
                />
            );
        case PAGES.RETAILER.ORDER.ADDITIONAL_SERVICES.URL:
            return (
                <NextButton
                    disabled={!isCustomerStepComplete}
                    title="Neste: oppsummering"
                    onClick={() => window.location.assign(PAGES.RETAILER.ORDER.SUMMARY.URL)}
                />
            );
        case PAGES.RETAILER.ORDER.EQUIPMENT.URL:
            return (
                <NextButton
                    disabled={!isCustomerStepComplete}
                    title="Neste: tilleggstjenester"
                    onClick={() => window.location.assign(PAGES.RETAILER.ORDER.ADDITIONAL_SERVICES.URL)}
                />
            );
        default:
            return null;
    }
};

interface BackButtonProps {
    path: string;
}

const BackButton: React.FC<BackButtonProps> = () => {
    const href = "";
    const text = "Tilbake";

    return <BackLink href={href} text={text} />;
};

interface ContinueSectionProps {
    className?: string;
    path: string;
}

const ContinueSection: React.FC<ContinueSectionProps> = ({ className = "", path }) =>
    path.includes("oppsummering") || path.includes("kvittering") ? null : (
        <Container
            className={classnames("retailer-continue-section", {
                [className]: className,
            })}
            size="small"
            noMargin
        >
            <BackButton path={path} />
            <div className="retailer-continue-section-button-container">
                <ContinueButton path={path} />
            </div>
        </Container>
    );

export default ContinueSection;
